// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React from "react";
import { Navigate } from "react-router-dom";
import { useKeycloak } from "../../context/KeycloakProvider";
import { usePermissions } from "../../hooks/usePermissions";
import ProtectedRoute from "./ProtectedRoute";
import { ROUTE_TYPES, getFirstAccessibleRoute } from "../../routes/routeConfig";
import env from "../../utils/env";

const RouteRenderer = ({ route }) => {
  const { authenticated, getUserInfo } = useKeycloak();
  const { userHasRoles } = usePermissions();

  const userInfo = getUserInfo();
  const resourceName = env.REACT_APP_KEYCLOAK_CLIENT_ID;
  const keycloakRoles = userInfo?.resourceRoles?.[resourceName]?.roles || [];

  if (route.redirect) {
    return <Navigate to={route.redirect} replace />;
  }

  if (route.type === ROUTE_TYPES.PUBLIC) {
    if (route.path === "/") {
      if (authenticated && userHasRoles) {
        const firstAccessibleRoute = getFirstAccessibleRoute(keycloakRoles);
        return <Navigate to={firstAccessibleRoute} replace />;
      }

      const Component = route.element;
      return <Component />;
    }

    if (authenticated && route.redirectIfAuthenticated !== null) {
      if (!userHasRoles) {
        return <Navigate to="/?showPermissionPopup=true" replace />;
      }

      const firstAccessibleRoute = getFirstAccessibleRoute(keycloakRoles);
      return <Navigate to={firstAccessibleRoute} replace />;
    }

    const Component = route.element;
    return <Component />;
  }

  if (
    route.type === ROUTE_TYPES.PROTECTED ||
    route.type === ROUTE_TYPES.ADMIN ||
    route.type === ROUTE_TYPES.USER
  ) {
    const Component = route.element;
    const Layout = route.layout;

    return (
      <ProtectedRoute
        roles={route.roles || []}
        permissions={route.permissions || []}
      >
        {Layout ? (
          <Layout>
            <Component />
          </Layout>
        ) : (
          <Component />
        )}
      </ProtectedRoute>
    );
  }

  return <Navigate to="/" replace />;
};

export default RouteRenderer;
